import { MsalProvider } from '@azure/msal-react';
import React, { useState, useEffect } from 'react';
import useBotPagesConfig from '../hooks/useBotPagesConfig';
import {
    AuthenticationResult,
    EventMessage,
    EventType,
    PublicClientApplication
} from '@azure/msal-browser';
import isNullOrWhiteSpace from '../lib/isNullOrWhiteSpace';
import { Container } from 'reactstrap';

// https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/2139

/*
    The difficulty here is that msal assumes you are on the same sub-domain when authenticating and the redirect URI.

    The redirect URIs are a set allowed list that you add into the application registration  So, for example, if you are on https://tom.domain.com then
    for MSAL to work its magic requires https://tom.domain.com to be set against the app registration.

    For help pages we can create as many of these as we want and remove them again.  So instead of doing this, we redirect everyone to auth.domain.com then back again.

    We keep track of the original sub-domain in the state which gets passed back to us in the post back.  We then parse this out and redirect back.

    We need to go back to the original domain so that MSAL can complete the loop as it stores state in some kind of storage (i.e. session/local/cookie) that does not work cross domain
*/

/**
 *
 * @returns The top level subdomain. i.e. 'customer1' from https://customer1.helpfruit.com
 */
const getTld = () => {
    return window.location.hostname.split('.').slice(1).join('.');
};

/**
 *
 * @returns For getting the port on localhost
 */
const getOptionalPort = () => {
    return window.location.port ? `:${window.location.port}` : '';
};

/**
 *
 * @returns Gets the sub domain from a full domain: i.e. "sub" from sub.dev.com
 */
const getSubDomainFromHash = () => {
    return window.location.hash.split('&')[2].split('=')[1].split('%7c')[1];
};

/**
 *
 * @returns Redirects auth from auth.domain.com => originalHost.domain.com
 *          The originalHost comes from the state in the window hash
 */
const redirect = async () => {
    window.location.replace(
        'https://' +
            getSubDomainFromHash() +
            '.' +
            getTld() +
            getOptionalPort() +
            window.location.hash
    );
};

/**
 *
 * @returns The domain for azure application registration to reply back to
 */
const getRedirectDomain = () => {
    if (window.location.hostname === 'localhost') {
        return `https://${window.location.host}`;
    }

    return `https://auth.${getTld()}${getOptionalPort()}`;
};

const redirectUri = getRedirectDomain();

const MsalWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { clientId, error, isLoading } = useBotPagesConfig();
    const [msalInstance, setMsalInstance] =
        useState<PublicClientApplication | null>(null);

    useEffect(() => {
        if (isLoading) {
            return;
        }

        if (isNullOrWhiteSpace(clientId)) {
            return;
        }
        // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
        const msalConfig = {
            auth: {
                clientId: clientId,
                authority: `https://login.microsoftonline.com/common`,
                redirectUri: redirectUri,
                postLogoutRedirectUri: redirectUri
            }
        };

        const msalInstance = new PublicClientApplication(msalConfig);

        msalInstance.initialize().then(() => {
            const accounts = msalInstance.getAllAccounts();
            if (accounts.length > 0) {
                msalInstance.setActiveAccount(accounts[0]);
            }
            msalInstance.enableAccountStorageEvents();

            msalInstance.addEventCallback(async (event: EventMessage) => {
                if (
                    event.eventType === EventType.LOGIN_SUCCESS &&
                    event.payload
                ) {
                    const payload = event.payload as AuthenticationResult;
                    const account = payload.account;
                    msalInstance.setActiveAccount(account);

                    if (window?.faqbot?.setContext) {
                        const context = {
                            email: account.username || '',
                            'user name': account.name || ''
                        };

                        window.faqbot.setContext(context);
                    }
                }
            });

            setMsalInstance(msalInstance);
        });
    }, [isLoading, clientId]);

    if (isLoading) {
        return (
            <Container>
                <p>Loading configuration...</p>
            </Container>
        );
    }

    if (error) {
        return (
            <Container>
                <p>Error loading configuration: {error}</p>
            </Container>
        );
    }

    if (isNullOrWhiteSpace(clientId)) {
        return (
            <Container>
                <p>No client configuration available</p>
            </Container>
        );
    }

    if (!msalInstance) {
        return (
            <Container>
                <p>Initializing authentication...</p>
            </Container>
        );
    }

    // If we get here on the auth subdomain, then we need to redirect back to the main domain to complete the MSAL signup
    if (window.location.hostname.split('.')[0] == 'auth') {
        redirect();
    }

    return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
};

export default MsalWrapper;
